import { useEffect, useState } from "react";
import type { PlaceDetail, PlaceListItem } from "@atlas/contracts";
import { addPlaceNote, addPlaceVisit, ApiError, deletePlace, fetchPlace, fetchPlaces, updatePlace } from "../../lib/api";
import { statusLabel } from "../../lib/statusLabels";
import { statusTone } from "../../lib/statusTone";
import { formatNumberFa } from "../../lib/format";
import { AppLink } from "../AppLink";
import { DomainListPage } from "../DomainListPage";
import { DomainDetailPage } from "../DomainDetailPage";
import { DetailActionMenu } from "../DetailActionMenu";
import { RatingBadge, RatingDot } from "../RatingBadge";
import { Badge } from "../ui/Badge";
import { Sheet } from "../ui/Sheet";
import { AlertDialog } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { Field, Input, Textarea } from "../ui/Input";
import { ChipGroup } from "../ui/Select";
import { useToast } from "../ui/Toast";
import { Icon } from "../Icon";

const PAGE_SIZE = 20;
const PLACE_STATUSES: Array<{ value: "" | "want" | "visited" | "again"; label: string }> = [
  { value: "", label: "همه وضعیت‌ها" },
  { value: "want", label: "می‌خواهم بروم" },
  { value: "visited", label: "رفته‌ام" },
  { value: "again", label: "دوباره می‌روم" },
];
const PLACE_CATEGORIES: Array<{ value: "" | "food" | "cafe" | "entertainment" | "travel"; label: string }> = [
  { value: "", label: "همه دسته‌ها" },
  { value: "food", label: "غذا" },
  { value: "cafe", label: "کافه" },
  { value: "entertainment", label: "سرگرمی" },
  { value: "travel", label: "سفر" },
];

interface PlacesListPageProps {
  onNavigate: (path: string) => void;
  onOpenSearch: () => void;
}

export function PlacesListPage({ onNavigate, onOpenSearch }: PlacesListPageProps) {
  const [status, setStatus] = useState<"" | "want" | "visited" | "again">("");
  const [category, setCategory] = useState<"" | "food" | "cafe" | "entertainment" | "travel">("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<PlaceListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchPlaces(
      {
        status: status || undefined,
        category: category || undefined,
        q: query.trim() || undefined,
        limit: PAGE_SIZE,
        offset: 0,
      },
      controller.signal,
    )
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setItems(null);
        setError(err instanceof ApiError ? err.message : "بارگذاری مکان‌ها با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [status, category, query, reloadToken]);

  const statusText = PLACE_STATUSES.find((s) => s.value === status)?.label ?? "همه وضعیت‌ها";
  const categoryText = PLACE_CATEGORIES.find((c) => c.value === category)?.label ?? "همه دسته‌ها";
  const filterSummary = `${categoryText} · ${statusText} · ${formatNumberFa(total)} مورد`;

  return (
    <>
      <DomainListPage
        title="مکان‌ها"
        onOpenSearch={onOpenSearch}
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="جست‌وجو در مکان‌ها"
        onOpenFilterSheet={() => setFilterOpen(true)}
        filterActive={status !== "" || category !== ""}
        filterSummary={filterSummary}
        items={items}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((t) => t + 1)}
        emptyTitle="مکانی با این فیلتر پیدا نشد"
        emptyHint="فیلتر را تغییر بده یا پاک کن"
        getKey={(item) => item.id}
        renderItem={(item) => (
          <AppLink href={`/places/${item.slug}`} onNavigate={onNavigate} className="item-row">
            <span className="item-row__media item-row__media--place">عکس</span>
            <span className="item-row__body">
              <span className="item-row__top">
                <span className="item-row__title">{item.title}</span>
                <Badge tone={statusTone(item.status)}>{statusLabel(item.status)}</Badge>
              </span>
              <span className="item-row__meta">
                <span className="chip">{PLACE_CATEGORIES.find((c) => c.value === item.category)?.label}</span>
                <span>{item.area}</span>
              </span>
              <span className="item-row__meta">
                <RatingDot rating={item.rating} />
              </span>
            </span>
          </AppLink>
        )}
      />
      <Sheet open={filterOpen} onClose={() => setFilterOpen(false)} title="فیلتر و مرتب‌سازی مکان‌ها">
        <div className="filter-group">
          <p className="filter-group__label">دسته</p>
          <ChipGroup options={PLACE_CATEGORIES} value={category} onChange={setCategory} ariaLabel="دسته" />
        </div>
        <div className="filter-group">
          <p className="filter-group__label">وضعیت</p>
          <ChipGroup options={PLACE_STATUSES} value={status} onChange={setStatus} ariaLabel="وضعیت" />
        </div>
        <div className="sheet-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setStatus("");
              setCategory("");
            }}
          >
            پاک‌کردن فیلترها
          </Button>
          <Button variant="primary" onClick={() => setFilterOpen(false)}>
            اعمال فیلتر
          </Button>
        </div>
      </Sheet>
    </>
  );
}

interface PlaceDetailPageProps {
  slug: string;
  onNavigate: (path: string) => void;
}

type ActiveSheet = "edit" | "status" | "note" | "visit" | null;

export function PlaceDetailPage({ slug, onNavigate }: PlaceDetailPageProps) {
  const [place, setPlace] = useState<PlaceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [activeSheet, setActiveSheet] = useState<ActiveSheet>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const [formTitle, setFormTitle] = useState("");
  const [formArea, setFormArea] = useState("");
  const [formStatus, setFormStatus] = useState<"want" | "visited" | "again">("want");
  const [formNote, setFormNote] = useState("");
  const [formVisitNote, setFormVisitNote] = useState("");
  const [formVisitRating, setFormVisitRating] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchPlace(slug, controller.signal)
      .then(setPlace)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setPlace(null);
        setError(err instanceof ApiError ? err.message : "بارگذاری مکان با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [slug, reloadToken]);

  function openEdit() {
    if (!place) return;
    setFormTitle(place.title);
    setFormArea(place.area);
    setActiveSheet("edit");
  }

  function openStatus() {
    if (!place) return;
    setFormStatus(place.status);
    setActiveSheet("status");
  }

  async function submitEdit() {
    if (!place) return;
    setBusy(true);
    try {
      const updated = await updatePlace(place.slug, { title: formTitle.trim() || undefined, area: formArea });
      setPlace(updated);
      setActiveSheet(null);
      toast.push("تغییرات ذخیره شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ذخیره با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function submitStatus() {
    if (!place) return;
    setBusy(true);
    try {
      const updated = await updatePlace(place.slug, { status: formStatus });
      setPlace(updated);
      setActiveSheet(null);
      toast.push("وضعیت به‌روزرسانی شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "تغییر وضعیت با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function submitNote() {
    if (!place || !formNote.trim()) return;
    setBusy(true);
    try {
      const note = await addPlaceNote(place.slug, { body: formNote.trim() });
      setPlace((prev) => (prev ? { ...prev, placeNotes: [note, ...prev.placeNotes] } : prev));
      setFormNote("");
      setActiveSheet(null);
      toast.push("یادداشت افزوده شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "افزودن یادداشت با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function submitVisit() {
    if (!place || !formVisitNote.trim()) return;
    setBusy(true);
    try {
      const rating = formVisitRating.trim() ? Number(formVisitRating) : undefined;
      const visit = await addPlaceVisit(place.slug, { note: formVisitNote.trim(), rating });
      setPlace((prev) => (prev ? { ...prev, visits: [visit, ...prev.visits] } : prev));
      setFormVisitNote("");
      setFormVisitRating("");
      setActiveSheet(null);
      toast.push("بازدید ثبت شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ثبت بازدید با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function confirmAndDelete() {
    if (!place) return;
    setBusy(true);
    try {
      await deletePlace(place.slug);
      toast.push("مکان حذف شد.");
      onNavigate("/places");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "حذف با خطا مواجه شد.", "danger");
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  return (
    <>
      <DomainDetailPage
        backHref="/places"
        backLabel="بازگشت"
        onNavigate={onNavigate}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((t) => t + 1)}
        heroVariant="photo"
        hero={place?.assetUrl ? (
          <img src={place.assetUrl} alt={place?.title} className="detail-hero__photo" />
        ) : (
          <span className="detail-hero__photo">عکس مکان</span>
        )}
      >
        {place && (
          <div style={{ padding: "0 1.1rem" }}>
            <h1 className="detail-title">{place.title}</h1>
            <p className="detail-subtitle">{place.area}</p>

            <div className="detail-badges">
              <Badge tone={statusTone(place.status)}>{statusLabel(place.status)}</Badge>
              <span className="badge badge--outline">
                {PLACE_CATEGORIES.find((c) => c.value === place.category)?.label}
              </span>
              <RatingBadge rating={place.rating} />
            </div>

            {place.neshanUrl ? (
              <a className="external-link" href={place.neshanUrl} target="_blank" rel="noopener noreferrer">
                باز کردن در نشان
              </a>
            ) : (
              <p className="external-link-hint" style={{ marginBottom: 14 }}>
                پیوند نشان برای این مکان ثبت نشده است.
              </p>
            )}

            <div className="info-card__actions" style={{ marginBottom: 20 }}>
              <button type="button" className="info-card__action" onClick={() => setActiveSheet("visit")}>
                ثبت بازدید
              </button>
            </div>

            {place.notes && (
              <div className="section">
                <p className="section__label">
                  <Icon name="notebook" width={13} height={13} aria-hidden="true" />
                  یادداشت
                </p>
                <p className="notes-box">{place.notes}</p>
              </div>
            )}

            {place.placeNotes.length > 0 && (
              <div className="section">
                <p className="section__label">
                  <Icon name="notebook" width={13} height={13} aria-hidden="true" />
                  یادداشت‌ها
                </p>
                <div className="log-list">
                  {place.placeNotes.map((n) => (
                    <p key={n.id} className="notes-box">
                      {n.body}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className="section">
              <p className="section__label">
                <Icon name="clock" width={13} height={13} aria-hidden="true" />
                بازدیدها
              </p>
              {place.visits.length === 0 && <p className="notes-box notes-box--muted">هنوز بازدیدی ثبت نشده.</p>}
              {place.visits.length > 0 && (
                <div className="visit-list">
                  {place.visits.map((v) => (
                    <div key={v.id} className="visit-row">
                      <span className="visit-row__date">{new Date(v.visitedAt).toLocaleDateString("fa-IR")}</span>
                      <span className="visit-row__note">{v.note}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DomainDetailPage>

      {place && (
        <DetailActionMenu
          actions={[
            { label: "ویرایش", icon: "notebook", onClick: openEdit },
            { label: "تغییر وضعیت", icon: "check", onClick: openStatus },
            { label: "افزودن یادداشت", icon: "notebook", onClick: () => setActiveSheet("note") },
            { label: "حذف", icon: "x", onClick: () => setConfirmDelete(true), danger: true },
          ]}
        />
      )}

      <Sheet open={activeSheet === "edit"} onClose={() => setActiveSheet(null)} title="ویرایش مکان">
        <Field label="نام">
          <Input value={formTitle} onChange={(e) => setFormTitle(e.target.value)} />
        </Field>
        <Field label="منطقه">
          <Input value={formArea} onChange={(e) => setFormArea(e.target.value)} />
        </Field>
        <Button variant="primary" block onClick={submitEdit} disabled={busy} style={{ marginTop: 14 }}>
          ذخیره
        </Button>
      </Sheet>

      <Sheet open={activeSheet === "status"} onClose={() => setActiveSheet(null)} title="تغییر وضعیت">
        <ChipGroup
          options={[
            { label: "می‌خواهم بروم", value: "want" },
            { label: "رفته‌ام", value: "visited" },
            { label: "دوباره می‌روم", value: "again" },
          ]}
          value={formStatus}
          onChange={(v) => setFormStatus(v as typeof formStatus)}
          ariaLabel="وضعیت"
        />
        <Button variant="primary" block onClick={submitStatus} disabled={busy} style={{ marginTop: 14 }}>
          ذخیره
        </Button>
      </Sheet>

      <Sheet open={activeSheet === "note"} onClose={() => setActiveSheet(null)} title="افزودن یادداشت">
        <Field label="یادداشت">
          <Textarea rows={4} value={formNote} onChange={(e) => setFormNote(e.target.value)} />
        </Field>
        <Button variant="primary" block onClick={submitNote} disabled={busy || !formNote.trim()} style={{ marginTop: 14 }}>
          افزودن
        </Button>
      </Sheet>

      <Sheet open={activeSheet === "visit"} onClose={() => setActiveSheet(null)} title="ثبت بازدید">
        <Field label="یادداشت بازدید">
          <Textarea rows={3} value={formVisitNote} onChange={(e) => setFormVisitNote(e.target.value)} />
        </Field>
        <Field label="امتیاز شخصی این بازدید (۰ تا ۱۰، اختیاری)">
          <Input type="number" dir="ltr" min={0} max={10} value={formVisitRating} onChange={(e) => setFormVisitRating(e.target.value)} />
        </Field>
        <Button
          variant="primary"
          block
          onClick={submitVisit}
          disabled={busy || !formVisitNote.trim()}
          style={{ marginTop: 14 }}
        >
          ثبت بازدید
        </Button>
      </Sheet>

      <AlertDialog
        open={confirmDelete}
        title="حذف مکان"
        message={`آیا از حذف «${place?.title ?? ""}» مطمئن هستید؟ این کار قابل بازگشت نیست.`}
        confirmLabel="حذف"
        destructive
        busy={busy}
        onConfirm={confirmAndDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}
